import React, { useEffect, useMemo, useState } from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";
import MetricTrendChart from "../charts/MetricTrendChart";
import { useDateTimeFormat } from "../../hooks/useDateTimeFormat";
import { buildTrendRegressionLine } from "../../utils/charts/trendRegression";
import { getTertiaryColour } from "../../utils/getTertiaryColour";
import {
  filterPatientRecordingPresentation,
} from "../../utils/patientPresentationFeatures";
import {
  buildRecordingPresentation,
  loadPatientRecordingDocument,
} from "./PatientRecordingOverlay";
import {
  formatQuestionnaireTypeFallback,
  getQuestionnaireTypeDefinition,
} from "../../utils/patientProfileData";

function normaliseStatus(value) {
  if (typeof value !== "string") return "fail";

  const normalized = value.trim().toLowerCase();

  if (
    normalized === "pass" ||
    normalized === "passed" ||
    normalized === "good" ||
    normalized === "very good"
  ) {
    return "pass";
  }

  return "fail";
}

function statusToTrendScore(value) {
  return normaliseStatus(value) === "pass" ? 1 : 0;
}

function formatTrendStatusScore(value, t) {
  const numericValue = Number(value);

  if (!Number.isFinite(numericValue)) {
    return t("profilePage.statusFail", "Fail");
  }

  if (numericValue > 0 && numericValue < 1) {
    return t("profilePage.measurementTrendPassRate", "{{percent}}% pass", {
      percent: Math.round(numericValue * 100),
    });
  }

  return Math.round(Number(value)) >= 1
    ? t("profilePage.statusPass", "Pass")
    : t("profilePage.statusFail", "Fail");
}

function getSortableTimestamp(value) {
  const ms = new Date(value || "").getTime();
  return Number.isFinite(ms) ? ms : Number.POSITIVE_INFINITY;
}

function isFiniteTrendValue(value) {
  return typeof value === "number" && Number.isFinite(value);
}

const FAIL_POINT_COLOUR = "#F59E0B";
const TREND_DIRECTION_RELATIVE_THRESHOLD = 0.01;
const TREND_DIRECTION_QUALITY_ABSOLUTE_THRESHOLD = 0.05;
const DAY_IN_MS = 24 * 60 * 60 * 1000;
const DEFAULT_TREND_TIMEFRAME = "3m";
const TREND_TIMEFRAME_DURATIONS = {
  "1w": 7 * DAY_IN_MS,
  "2w": 14 * DAY_IN_MS,
  "1m": 30 * DAY_IN_MS,
  "3m": 90 * DAY_IN_MS,
  "6m": 180 * DAY_IN_MS,
  "1y": 365 * DAY_IN_MS,
  all: null,
};

function getAverageValue(values) {
  if (!Array.isArray(values) || !values.length) {
    return null;
  }

  const sum = values.reduce((total, value) => total + value, 0);
  return sum / values.length;
}

const CHART_PRIORITY = {
  "metric:rr": -1000,
  "metric:meanExhaleInhaleAreaRatio": 800,
  "metric:meanRectangularAreaRatio": 810,
};

// Trend charts that should never render a negative Y-axis. Questionnaire
// scores/items are handled as a source kind below; this set keeps
// respiratory-rate plots anchored at zero even when Chart.js adds padding
// around sparse or flat series.
const ZERO_FLOORED_TREND_KEYS = new Set([
  "metric:rr",
  "metric:rrMin",
  "metric:rrMax",
]);

// Keep questionnaire score trends grouped ahead of every measurement metric so
// they always render first, preserving their observed order among themselves.
const QUESTIONNAIRE_PRIORITY_BASE = -100000;

function getChartPriority(chart) {
  if (chart.sourceKind === "questionnaire") {
    return QUESTIONNAIRE_PRIORITY_BASE + (chart.sortOrder || 0);
  }
  if (chart.kind === "quality") return 1000 + (chart.sortOrder || 0);
  if (Object.prototype.hasOwnProperty.call(CHART_PRIORITY, chart.key)) {
    return CHART_PRIORITY[chart.key];
  }
  return chart.sortOrder || 0;
}

function getChartYAxisMin(chart) {
  if (chart.kind === "quality") {
    return -0.1;
  }

  if (chart.sourceKind === "questionnaire") {
    return 0;
  }

  return ZERO_FLOORED_TREND_KEYS.has(chart.key) ? 0 : undefined;
}

function getChartTrendDirection(chart) {
  const trendLinePoints = Array.isArray(chart?.trendLinePoints)
    ? chart.trendLinePoints
    : chart?.recordingPoints;
  const regression = buildTrendRegressionLine(trendLinePoints);

  if (!regression) {
    return "flat";
  }

  const delta = regression.delta;
  const precisionThreshold =
    chart.kind === "quality"
      ? TREND_DIRECTION_QUALITY_ABSOLUTE_THRESHOLD
      : 10 ** -(chart.yDecimals ?? 0);
  const magnitudeThreshold =
    Math.max(Math.abs(regression.yMin), Math.abs(regression.yMax), 1) *
    TREND_DIRECTION_RELATIVE_THRESHOLD;
  const threshold = Math.max(precisionThreshold, magnitudeThreshold);

  if (Math.abs(delta) <= threshold) {
    return "flat";
  }

  return delta > 0 ? "up" : "down";
}

function getChartTrendIconConfig(chart, t) {
  const direction = getChartTrendDirection(chart);

  if (direction === "up") {
    return {
      direction,
      icon: "trending_up",
      label: t("profilePage.measurementTrendDirectionUp", "Trend increasing"),
    };
  }

  if (direction === "down") {
    return {
      direction,
      icon: "trending_down",
      label: t("profilePage.measurementTrendDirectionDown", "Trend decreasing"),
    };
  }

  return {
    direction,
    icon: "trending_flat",
    label: t("profilePage.measurementTrendDirectionFlat", "Trend flat"),
  };
}

function shouldIncludeValueInAverage(chart, record) {
  if (chart.kind === "quality") {
    return true;
  }

  if (chart.sourceKind === "questionnaire") {
    return true;
  }

  return record.pointQuality !== "fail";
}

function getChartSectionKey(chart) {
  if (chart.sourceKind === "questionnaire") {
    return "questionnaires";
  }

  if (chart.kind === "quality") {
    return "quality";
  }

  return "measurements";
}

function getTrendPointQuality(presentation) {
  const statusesByKey = new Map(
    (presentation?.statuses || [])
      .filter((status) => status?.key)
      .map((status) => [status.key, status.rawValue]),
  );

  const qualityValue =
    statusesByKey.get("signalQuality") ??
    statusesByKey.get("respiratoryQuality") ??
    statusesByKey.get("spirometryQuality") ??
    statusesByKey.get("cardiacQuality");

  if (qualityValue == null) {
    return null;
  }

  return normaliseStatus(qualityValue);
}

function hasTrendStatusValue(value) {
  return typeof value === "string" && value.trim().length > 0;
}

function buildTrendRecord(entry, presentation, formatDate, prefix = "") {
  const xValue = getSortableTimestamp(entry?.takenAt);
  const dayLabel = formatDate(entry?.takenAt) || "--";

  return {
    entry,
    presentation,
    pointQuality: getTrendPointQuality(presentation),
    dayKey:
      dayLabel === "--"
        ? `${prefix}${entry?.uid || "unknown"}:${entry?.name || xValue}`
        : `${prefix}${dayLabel}`,
    dayLabel,
    xValue,
  };
}

function buildMeasurementTrendPresentationFromSummary(entry, t) {
  const summary =
    entry?.resultSummary && typeof entry.resultSummary === "object"
      ? entry.resultSummary
      : null;

  if (!summary) {
    if (
      entry?.collectionName === "RecordingManualAudio" ||
      entry?.variant === "RecordingManualAudio"
    ) {
      return {
        metrics: [],
        statuses: [],
        trendMetrics: [],
      };
    }

    return null;
  }

  if (summary.kind === "manual-audio") {
    return {
      metrics: [],
      statuses: [],
      trendMetrics: [],
    };
  }

  if (summary.kind === "spirometry") {
    return {
      metrics: [
        {
          key: "fev1",
          featureKey: "fev1",
          label: "FEV1",
          rawValue: summary.spirometry?.fev1 ?? null,
          unit: "L",
          trendDecimals: 2,
        },
        {
          key: "fvc",
          featureKey: "fvc",
          label: "FVC",
          rawValue: summary.spirometry?.fvc ?? null,
          unit: "L",
          trendDecimals: 2,
        },
        {
          key: "fev1FvcRatio",
          featureKey: "fev1Fvc",
          label: "FEV1/FVC",
          rawValue: summary.spirometry?.fev1FvcRatio ?? null,
          unit: null,
          trendDecimals: 2,
        },
        {
          key: "pef",
          featureKey: "pef",
          label: "PEF",
          rawValue: summary.spirometry?.pef ?? null,
          unit: "L/min",
          trendDecimals: 0,
        },
        {
          key: "rr",
          featureKey: "rr",
          label: "RR",
          rawValue: summary.respiratoryRateBpm ?? null,
          unit: "bpm",
          trendDecimals: 0,
        },
      ],
      statuses: [
        {
          key: "signalQuality",
          featureKey: "signalQuality",
          label: t("profilePage.signalQuality", "Signal quality"),
          rawValue: summary.signalQualityStatus ?? null,
        },
        {
          key: "spirometryQuality",
          featureKey: "spirometryQuality",
          label: t("profilePage.spirometryQuality", "Spirometry quality"),
          rawValue: summary.spiroSignalQualityStatus ?? null,
        },
      ],
      trendMetrics: [
        {
          key: "meanExhaleInhaleAreaRatio",
          featureKey: "relativeChange",
          label: t(
            "profilePage.meanExhaleInhaleAreaRatio",
            "Mean Exhale/Inhale Area Ratio",
          ),
          rawValue:
            summary.spirometry?.additionalMetrics?.meanExhaleInhaleAreaRatio ??
            null,
          unit: null,
          trendDecimals: 3,
        },
        {
          key: "meanRectangularAreaRatio",
          featureKey: "relativeChange",
          label: t(
            "profilePage.meanRectangularAreaRatio",
            "Mean Rectangular Area Ratio",
          ),
          rawValue:
            summary.spirometry?.additionalMetrics?.meanRectangularAreaRatio ??
            null,
          unit: null,
          trendDecimals: 3,
        },
      ],
    };
  }

  return {
    metrics: [
      {
        key: "hr",
        featureKey: "hr",
        label: "HR",
        rawValue: summary.heartRateBpm ?? null,
        unit: "bpm",
        trendDecimals: 0,
      },
      {
        key: "hrv",
        featureKey: "hrv",
        label: "HRV",
        rawValue: summary.heartRateVariabilityMs ?? null,
        unit: "ms",
        trendDecimals: 0,
      },
      {
        key: "rr",
        featureKey: "rr",
        label: "RR",
        rawValue: summary.respiratoryRateBpm ?? null,
        unit: "bpm",
        trendDecimals: 0,
      },
      {
        key: "rrMin",
        featureKey: "rr",
        label: t("profilePage.rrMin", "RR Min"),
        rawValue: summary.respiratoryRateMinBpm ?? null,
        unit: "bpm",
        trendDecimals: 0,
      },
      {
        key: "rrMax",
        featureKey: "rr",
        label: t("profilePage.rrMax", "RR Max"),
        rawValue: summary.respiratoryRateMaxBpm ?? null,
        unit: "bpm",
        trendDecimals: 0,
      },
    ],
    statuses: [
      {
        key: "cardiacQuality",
        featureKey: "cardiacQuality",
        label: t("profilePage.cardiacQuality", "Cardiac quality"),
        rawValue: summary.cardiacQualityStatus ?? null,
      },
      {
        key: "respiratoryQuality",
        featureKey: "signalQuality",
        label: t("profilePage.respiratoryQuality", "Respiratory quality"),
        rawValue: summary.respiratoryQualityStatus ?? null,
      },
    ],
    trendMetrics: [],
  };
}

function getQuestionnaireTrendLabel(type, t) {
  const definition = getQuestionnaireTypeDefinition(type);

  if (definition) {
    return t(definition.labelKey, definition.labelDefault);
  }

  return formatQuestionnaireTypeFallback(type);
}

function buildQuestionnaireScoreTrendMetric(type, t) {
  const normalizedType =
    String(type || "")
      .trim()
      .toLowerCase() || "questionnaire";

  if (normalizedType === "cat") {
    return {
      key: "catScore",
      label: t("profilePage.catScore", "CAT score"),
    };
  }

  if (normalizedType === "sgrq") {
    return {
      key: "sgrqScore",
      label: t("profilePage.sgrqScore", "SGRQ score"),
    };
  }

  return {
    key: `${normalizedType}Score`,
    label: t("profilePage.questionnaireScore", "{{questionnaire}} score", {
      questionnaire: getQuestionnaireTrendLabel(type, t),
    }),
  };
}

function getQuestionnaireScore(entry) {
  const raw = entry?.score;

  if (raw == null || raw === "") {
    return null;
  }

  const numeric = Number(raw);
  return isFiniteTrendValue(numeric) ? numeric : null;
}

function buildQuestionnaireTrendRecords(
  questionnaires,
  formatDate,
  t,
  { allowScores = true } = {},
) {
  if (!allowScores) {
    return [];
  }

  if (!questionnaires || typeof questionnaires !== "object") {
    return [];
  }

  return Object.entries(questionnaires)
    .flatMap(([questionnaireType, questionnaireGroup]) => {
      const entries = Array.isArray(questionnaireGroup?.entries)
        ? questionnaireGroup.entries
        : [];

      return entries
        .map((entry) => {
          const score = getQuestionnaireScore(entry);
          const scoreMetric =
            score == null
              ? null
              : {
                  ...buildQuestionnaireScoreTrendMetric(questionnaireType, t),
                  rawValue: score,
                  unit: null,
                  trendDecimals: 0,
                  sourceKind: "questionnaire",
                };

          const metrics = [scoreMetric].filter(Boolean);

          if (!metrics.length) {
            return null;
          }

          return buildTrendRecord(
            entry,
            {
              metrics,
              statuses: [],
              trendMetrics: [],
            },
            formatDate,
            `${questionnaireType}:`,
          );
        })
        .filter(Boolean);
    })
    .sort((left, right) => left.xValue - right.xValue);
}

function buildTrendCharts(records) {
  const chartMap = new Map();

  const ensureChart = (config) => {
    if (!chartMap.has(config.key)) {
      chartMap.set(config.key, {
        ...config,
        sortOrder: chartMap.size,
        dayBuckets: new Map(),
        recordingPoints: [],
        missingCount: 0,
        observedCount: 0,
      });
    }

    return chartMap.get(config.key);
  };

  const ensureDayBucket = (chart, record) => {
    if (!chart.dayBuckets.has(record.dayKey)) {
      chart.dayBuckets.set(record.dayKey, {
        label: record.dayLabel,
        xValue: record.xValue,
        aggregatedValues: [],
        latestValue: null,
      });
    }

    return chart.dayBuckets.get(record.dayKey);
  };

  const pushBucketValue = (chart, record, value, options = {}) => {
    if (!isFiniteTrendValue(value)) return;

    const bucket = ensureDayBucket(chart, record);
    const includeInAverage =
      options.includeInAverage !== false &&
      shouldIncludeValueInAverage(chart, record);

    if (includeInAverage) {
      bucket.aggregatedValues.push(value);
      bucket.latestValue = value;
    }

    bucket.xValue = record.xValue;
    chart.recordingPoints.push({
      x: record.xValue,
      y: value,
      quality: record.pointQuality,
    });
  };

  records.forEach((record) => {
    record.presentation?.statuses?.forEach((status) => {
      if (!status?.key) return;

      const chart = ensureChart({
        key: `status:${status.key}`,
        label: status.label,
        kind: "quality",
        sourceKind: status.sourceKind || "measurement",
      });

      chart.observedCount += 1;

      if (!hasTrendStatusValue(status.rawValue)) {
        chart.missingCount += 1;
        return;
      }

      pushBucketValue(chart, record, statusToTrendScore(status.rawValue));
    });

    [
      ...(record.presentation?.metrics || []),
      ...(record.presentation?.trendMetrics || []),
    ].forEach((metric) => {
      if (!metric?.key) return;

      const chart = ensureChart({
        key: `metric:${metric.key}`,
        label: metric.label,
        kind: "numeric",
        unit: metric.unit || "",
        yDecimals: metric.trendDecimals ?? 0,
        sourceKind: metric.sourceKind || "measurement",
      });

      chart.observedCount += 1;

      if (!isFiniteTrendValue(metric.rawValue)) {
        chart.missingCount += 1;
        return;
      }

      pushBucketValue(chart, record, metric.rawValue);
    });
  });

  return Array.from(chartMap.values())
    .map((chart) => {
      const orderedBuckets = Array.from(chart.dayBuckets.values()).sort(
        (left, right) => left.xValue - right.xValue,
      );

      if (!orderedBuckets.length && chart.observedCount === 0) {
        return null;
      }

      return {
        ...chart,
        sectionKey: getChartSectionKey(chart),
        labels: orderedBuckets.map((bucket) => bucket.label),
        xValues: orderedBuckets.map((bucket) => bucket.xValue),
        values: orderedBuckets.map((bucket) =>
          getAverageValue(bucket.aggregatedValues),
        ),
        recordingPoints: chart.recordingPoints,
        minMax: orderedBuckets.map((bucket) => ({
          min: bucket.aggregatedValues.length
            ? Math.min(...bucket.aggregatedValues)
            : null,
          max: bucket.aggregatedValues.length
            ? Math.max(...bucket.aggregatedValues)
            : null,
        })),
        counts: orderedBuckets.map((bucket) => bucket.aggregatedValues.length),
        totalPoints: orderedBuckets.reduce(
          (sum, bucket) => sum + bucket.aggregatedValues.length,
          0,
        ),
        missingCount: chart.missingCount,
        observedCount: chart.observedCount,
      };
    })
    .filter(Boolean)
    .sort((left, right) => getChartPriority(left) - getChartPriority(right));
}

function buildSharedXDomain(records) {
  const xValues = records
    .map((record) => Number(record?.xValue))
    .filter((value) => Number.isFinite(value));

  if (!xValues.length) {
    return null;
  }

  return {
    min: Math.min(...xValues),
    max: Math.max(...xValues),
  };
}

function filterTrendRecordsByTimeframe(records, timeframe) {
  if (!Array.isArray(records) || !records.length) {
    return [];
  }

  const windowDuration = TREND_TIMEFRAME_DURATIONS[timeframe];

  if (!Number.isFinite(windowDuration)) {
    return records;
  }

  const xValues = records
    .map((record) => Number(record?.xValue))
    .filter((value) => Number.isFinite(value));

  if (!xValues.length) {
    return records;
  }

  const latestXValue = Math.max(...xValues);
  const cutoff = latestXValue - windowDuration;

  return records.filter((record) => {
    const xValue = Number(record?.xValue);
    return Number.isFinite(xValue) && xValue >= cutoff;
  });
}

function applyTrendWindowToCharts(charts, trendCharts) {
  const trendChartMap = new Map(
    (Array.isArray(trendCharts) ? trendCharts : []).map((chart) => [
      chart.key,
      chart,
    ]),
  );

  return (Array.isArray(charts) ? charts : []).map((chart) => {
    const trendChart = trendChartMap.get(chart.key);

    return {
      ...chart,
      trendValues: Array.isArray(trendChart?.values) ? trendChart.values : [],
      trendLinePoints: Array.isArray(trendChart?.recordingPoints)
        ? trendChart.recordingPoints
        : [],
    };
  });
}

function TrendChartCard({
  chart,
  t,
  formatTooltipTitle,
  formatTooltipLabel,
  xDomainOverride,
}) {
  const averageSeriesLabel = t(
    "profilePage.measurementTrendAverage",
    "Daily average",
  );
  const resultSeriesLabel = t("profilePage.measurementTrendResult", "Result");
  const recordingSeriesLabel = t(
    "profilePage.measurementTrendRecordings",
    "Recordings",
  );
  const measurementSeriesLabel = t(
    "profilePage.measurementTrendMeasurement",
    "Measurement",
  );
  const neutralSeriesLabel =
    chart.sourceKind === "questionnaire"
      ? resultSeriesLabel
      : recordingSeriesLabel;
  const passPointColour = getTertiaryColour();
  const finiteValueCount = chart.values.filter((value) =>
    Number.isFinite(value),
  ).length;
  const displayPointCount =
    chart.totalPoints || finiteValueCount || chart.observedCount;
  const extraDatasets = useMemo(() => {
    const points = Array.isArray(chart.recordingPoints)
      ? chart.recordingPoints
      : [];

    if (chart.kind === "quality") {
      if (!points.length) {
        return [];
      }

      return [
        {
          label: measurementSeriesLabel,
          data: points,
          color: passPointColour,
          pointRadius: 4,
          pointHoverRadius: 5,
          borderWidth: 0,
          showLine: false,
          order: -10,
        },
      ];
    }
    const passPoints = points.filter((point) => point.quality === "pass");
    const failPoints = points.filter((point) => point.quality === "fail");
    const neutralPoints = points.filter(
      (point) => point.quality !== "pass" && point.quality !== "fail",
    );

    const datasets = [];

    if (failPoints.length) {
      datasets.push({
        label: t("profilePage.statusFail", "Fail"),
        data: failPoints,
        color: FAIL_POINT_COLOUR,
        pointRadius: 4,
        pointHoverRadius: 5,
        borderWidth: 0,
        showLine: false,
        order: -20,
      });
    }

    if (passPoints.length) {
      datasets.push({
        label: t("profilePage.statusPass", "Pass"),
        data: passPoints,
        color: passPointColour,
        pointRadius: 4,
        pointHoverRadius: 5,
        borderWidth: 0,
        showLine: false,
        order: -10,
      });
    }

    if (neutralPoints.length) {
      datasets.push({
        label: neutralSeriesLabel,
        data: neutralPoints,
        color: passPointColour,
        pointRadius: 4,
        pointHoverRadius: 5,
        borderWidth: 0,
        showLine: false,
        order: -10,
      });
    }

    return datasets;
  }, [
    chart.kind,
    chart.recordingPoints,
    measurementSeriesLabel,
    neutralSeriesLabel,
    passPointColour,
    t,
  ]);
  const hasPlottableValues =
    chart.values.some((value) => Number.isFinite(value)) ||
    extraDatasets.some(
      (dataset) => Array.isArray(dataset.data) && dataset.data.length > 0,
    );
  const missingSummary =
    chart.missingCount > 0
      ? t(
          "profilePage.measurementTrendMissingValues",
          "{{count}} entries had no value for this trend.",
          { count: chart.missingCount },
        )
      : null;
  const trendIcon = getChartTrendIconConfig(chart, t);
  const trendLinePoints = Array.isArray(chart.trendLinePoints)
    ? chart.trendLinePoints
    : chart.recordingPoints;
  const showTrendLine = trendLinePoints.length >= 2;

  return (
    <section className="card p-4">
      <div className="flex items-start justify-between gap-2">
        <div>
          <h3 className="text-lg font-semibold text-typography">
            <span className="inline-flex items-center gap-2">
              <span
                title={trendIcon.label}
                className="material-symbols-outlined text-xl leading-none text-greyscale-3"
                aria-hidden="true"
              >
                {trendIcon.icon}
              </span>
              <span>{chart.label}</span>
            </span>
          </h3>
        </div>
        <div className="shrink-0 rounded-full bg-background px-3 py-1 text-xs font-medium text-greyscale-3">
          {t("profilePage.measurementTrendPoints", "{{count}} pts", {
            count: displayPointCount,
          })}
        </div>
      </div>

      {hasPlottableValues ? (
        <MetricTrendChart
          height={220}
          labels={chart.labels}
          values={chart.values}
          xValues={chart.xValues}
          xDomainOverride={xDomainOverride}
          minMax={chart.minMax}
          counts={chart.counts}
          extraDatasets={extraDatasets}
          trendLinePoints={trendLinePoints}
          xTickFormat="dd/MM"
          xMaxTicksLimit={12}
          lineLabel={averageSeriesLabel}
          yUnit={chart.kind === "quality" ? "" : chart.unit || ""}
          yDecimals={chart.kind === "quality" ? 0 : (chart.yDecimals ?? 0)}
          yMin={getChartYAxisMin(chart)}
          yMax={chart.kind === "quality" ? 1.1 : undefined}
          yTickStep={chart.kind === "quality" ? 1 : undefined}
          showMinMaxBars
          showTrendLine={showTrendLine}
          showLegend
          formatTooltipTitle={formatTooltipTitle}
          formatTooltipLabel={formatTooltipLabel}
          formatYValue={
            chart.kind === "quality"
              ? (value) => formatTrendStatusScore(value, t)
              : undefined
          }
        />
      ) : (
        <div className="mt-4 rounded-2xl bg-background px-4 py-8 text-center text-sm text-greyscale-3">
          {t(
            "profilePage.measurementTrendNoValues",
            "No values are available for this trend yet.",
          )}
        </div>
      )}

      {missingSummary ? (
        <p className="mt-3 text-sm text-greyscale-3">{missingSummary}</p>
      ) : null}
    </section>
  );
}

function TrendChartSection({
  title,
  charts,
  t,
  formatTooltipTitle,
  formatTooltipLabel,
  xDomainOverride,
}) {
  if (!charts.length) {
    return null;
  }

  return (
    <section className="flex flex-col gap-4">
      <div className="flex items-center gap-3">
        <h3 className="text-lg font-semibold text-typography">{title}</h3>
        <div className="h-px flex-1 bg-greyscale-1" />
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        {charts.map((chart) => (
          <TrendChartCard
            key={chart.key}
            chart={chart}
            t={t}
            formatTooltipTitle={formatTooltipTitle}
            formatTooltipLabel={formatTooltipLabel}
            xDomainOverride={xDomainOverride}
          />
        ))}
      </div>
    </section>
  );
}

function LoadingProgress({ completed, total, t }) {
  const hasProgress = total > 0;
  const progressPercent = hasProgress
    ? Math.round((completed / total) * 100)
    : null;

  return (
    <div className="mx-auto w-full max-w-xl ">
      <p className="text-base font-semibold text-typography">
        {t(
          "profilePage.loadingMeasurementTrends",
          "Loading measurement trends…",
        )}
      </p>

      {hasProgress ? (
        <>
          <div
            className="mt-4 h-3 overflow-hidden rounded-full bg-greyscale-1"
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={total}
            aria-valuenow={completed}
            aria-valuetext={t(
              "profilePage.loadingMeasurementTrendsProgress",
              "Fetching recordings {{completed}} / {{total}}",
              { completed, total },
            )}
          >
            <div
              className="h-full rounded-full bg-primary transition-[width] duration-200"
              style={{ width: `${progressPercent}%` }}
            />
          </div>

          <div className="mt-3 flex items-center justify-between gap-4 text-sm text-greyscale-3">
            <span>
              {t(
                "profilePage.loadingMeasurementTrendsProgress",
                "Fetching recordings {{completed}} / {{total}}",
                { completed, total },
              )}
            </span>
            <span className="font-medium text-typography">
              {progressPercent}%
            </span>
          </div>
        </>
      ) : null}
    </div>
  );
}

function LoadingSkeletonCard() {
  return (
    <div className="card p-4 animate-pulse">
      <div className="flex items-start justify-between gap-2">
        <div className="space-y-2">
          <div className="h-5 w-36 rounded bg-greyscale-1" />
          <div className="h-4 w-24 rounded bg-greyscale-1" />
        </div>
        <div className="h-7 w-16 rounded-full bg-greyscale-1" />
      </div>

      <div className="mt-6 h-56 rounded-2xl bg-greyscale-1" />
      <div className="mt-4 h-4 w-40 rounded bg-greyscale-1" />
    </div>
  );
}

function LoadingSkeletonGrid({ t }) {
  return (
    <div className="space-y-6" aria-busy="true" aria-live="polite">
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <LoadingSkeletonCard />
        <LoadingSkeletonCard />
        <LoadingSkeletonCard />
        <LoadingSkeletonCard />
      </div>
    </div>
  );
}

export default function PatientMeasurementTrendsOverlay({
  entries,
  questionnaires,
  loadingHistory = false,
  open,
  onClose,
  dashboardResultFeatures = null,
}) {
  const { t } = useTranslation();
  const { formatDate, formatDateTime } = useDateTimeFormat();
  const [selectedTimeframe, setSelectedTimeframe] = useState(
    DEFAULT_TREND_TIMEFRAME,
  );
  const [state, setState] = useState({
    loading: false,
    error: null,
    records: [],
    progressCompleted: 0,
    progressTotal: 0,
  });

  useEffect(() => {
    const measurementEntries = Array.isArray(entries) ? entries : [];
    const questionnaireGroups =
      questionnaires && typeof questionnaires === "object"
        ? questionnaires
        : {};
    const questionnaireTrendRecords = buildQuestionnaireTrendRecords(
      questionnaireGroups,
      formatDate,
      t,
    );

    if (
      !open ||
      (!measurementEntries.length && !questionnaireTrendRecords.length)
    ) {
      setState({
        loading: false,
        error: null,
        records: [],
        progressCompleted: 0,
        progressTotal: 0,
      });
      return;
    }

    if (loadingHistory) {
      setState({
        loading: false,
        error: null,
        records: [],
        progressCompleted: 0,
        progressTotal: 0,
      });
      return;
    }

    let cancelled = false;

    const loadTrends = async () => {
      const sortedEntries = [...measurementEntries].sort(
        (left, right) =>
          getSortableTimestamp(left?.takenAt) -
          getSortableTimestamp(right?.takenAt),
      );
      const preloadedTrendRecords = [];
      const unresolvedEntries = [];

      sortedEntries.forEach((entry) => {
        const preloadedPresentation = filterPatientRecordingPresentation(
          buildMeasurementTrendPresentationFromSummary(entry, t),
          dashboardResultFeatures,
        );

        if (preloadedPresentation) {
          preloadedTrendRecords.push(
            buildTrendRecord(entry, preloadedPresentation, formatDate),
          );
          return;
        }

        unresolvedEntries.push(entry);
      });

      if (!unresolvedEntries.length) {
        const trendRecords = [
          ...preloadedTrendRecords,
          ...questionnaireTrendRecords,
        ];

        setState({
          loading: false,
          error: null,
          records: trendRecords,
          progressCompleted: 0,
          progressTotal: 0,
        });
        return;
      }

      setState({
        loading: true,
        error: null,
        records: [],
        progressCompleted: 0,
        progressTotal: unresolvedEntries.length,
      });

      try {
        const results = await Promise.allSettled(
          unresolvedEntries.map(async (entry) => {
            try {
              const match = await loadPatientRecordingDocument(entry);
              const presentation = buildRecordingPresentation(
                { ...entry, variant: match.variant },
                match.data,
                t,
              );

              return buildTrendRecord(
                entry,
                filterPatientRecordingPresentation(
                  presentation,
                  dashboardResultFeatures,
                ),
                formatDate,
              );
            } finally {
              if (cancelled) return;

              setState((previousState) => {
                if (!previousState.loading) {
                  return previousState;
                }

                return {
                  ...previousState,
                  progressCompleted: Math.min(
                    previousState.progressCompleted + 1,
                    previousState.progressTotal,
                  ),
                };
              });
            }
          }),
        );

        if (cancelled) return;

        const fulfilledResults = results
          .filter((result) => result.status === "fulfilled")
          .map((result) => result.value);

        const trendRecords = [
          ...preloadedTrendRecords,
          ...fulfilledResults,
          ...questionnaireTrendRecords,
        ];

        setState({
          loading: false,
          error: null,
          records: trendRecords,
          progressCompleted: unresolvedEntries.length,
          progressTotal: unresolvedEntries.length,
        });
      } catch (error) {
        if (cancelled) return;

        setState({
          loading: false,
          error,
          records: [],
          progressCompleted: 0,
          progressTotal: 0,
        });
      }
    };

    loadTrends();

    return () => {
      cancelled = true;
    };
  }, [
    dashboardResultFeatures,
    entries,
    formatDate,
    loadingHistory,
    open,
    questionnaires,
    t,
  ]);

  const formatTooltipTitle = useMemo(() => () => "", []);

  const formatTooltipLabel = useMemo(
    () =>
      ({ datasetLabel, x, yFormatted, yUnit, label }) => {
        const dateTime = Number.isFinite(x)
          ? formatDateTime(x)
          : { date: label || "--", time: "" };
        const timestampLabel = [dateTime.date, dateTime.time]
          .filter(Boolean)
          .join(" ")
          .trim();
        const seriesLabel = datasetLabel ? ` (${datasetLabel})` : "";
        const prefix = `${timestampLabel || label || "--"}${seriesLabel}`;

        return yUnit
          ? `${prefix}: ${yFormatted} ${yUnit}`
          : `${prefix}: ${yFormatted}`;
      },
    [formatDateTime],
  );
  const timeframeOptions = useMemo(
    () => [
      {
        value: "1w",
        label: t("profilePage.measurementTrendsWindow1w", "1 week"),
      },
      {
        value: "2w",
        label: t("profilePage.measurementTrendsWindow2w", "2 weeks"),
      },
      {
        value: "1m",
        label: t("profilePage.measurementTrendsWindow1m", "1 month"),
      },
      {
        value: "3m",
        label: t("profilePage.measurementTrendsWindow3m", "3 months"),
      },
      {
        value: "6m",
        label: t("profilePage.measurementTrendsWindow6m", "6 months"),
      },
      {
        value: "1y",
        label: t("profilePage.measurementTrendsWindow1y", "1 year"),
      },
      {
        value: "all",
        label: t("profilePage.measurementTrendsWindowAll", "All time"),
      },
    ],
    [t],
  );
  const timeframeRecords = useMemo(
    () => filterTrendRecordsByTimeframe(state.records, selectedTimeframe),
    [selectedTimeframe, state.records],
  );
  const allCharts = useMemo(
    () => buildTrendCharts(state.records),
    [state.records],
  );
  const timeframeCharts = useMemo(
    () => buildTrendCharts(timeframeRecords),
    [timeframeRecords],
  );
  const visibleCharts = useMemo(
    () => applyTrendWindowToCharts(allCharts, timeframeCharts),
    [allCharts, timeframeCharts],
  );
  const xDomain = useMemo(
    () => buildSharedXDomain(state.records),
    [state.records],
  );
  const chartsBySection = useMemo(() => {
    return {
      questionnaires: visibleCharts.filter(
        (chart) => chart.sectionKey === "questionnaires",
      ),
      measurements: visibleCharts.filter(
        (chart) => chart.sectionKey === "measurements",
      ),
      quality: visibleCharts.filter((chart) => chart.sectionKey === "quality"),
    };
  }, [visibleCharts]);

  if (!open) {
    return null;
  }

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6">
      <button
        type="button"
        aria-label={t(
          "profilePage.closeMeasurementTrends",
          "Close measurement trends",
        )}
        className="absolute inset-0 cursor-default bg-black/45 backdrop-blur-sm"
        onClick={onClose}
      />

      <div className="relative z-10 flex h-[96vh] w-[96vw] max-w-none flex-col overflow-hidden rounded-3xl border border-greyscale-1 bg-background shadow-2xl">
        <div className="flex items-start justify-between gap-4 p-4 pb-0">
          <div className="min-w-0">
            <h2 className="text-2xl font-semibold text-typography">
              {t("profilePage.measurementTrendsTitle", "Trends")}
            </h2>
            <p className="mt-1 text-sm text-greyscale-3">
              {t(
                "profilePage.measurementTrendsSubtitle",
                "Trend charts for recording metrics and questionnaire scores.",
              )}
            </p>
          </div>

          <div className="flex shrink-0 items-stretch gap-2">
            <div className="relative flex self-stretch">
              <label htmlFor="measurement-trends-window" className="sr-only">
                {t("profilePage.measurementTrendsWindowLabel", "Trend window")}
              </label>
              <div className="primary-button pointer-events-none !h-full !min-h-0 !px-4 !py-2 !text-sm !flex-row !items-center !gap-1 whitespace-nowrap">
                <span className="">
                  {t("profilePage.measurementTrendsWindowShortLabel", "Trend")}
                </span>
                <span className="font-semibold">
                  {timeframeOptions.find(
                    (option) => option.value === selectedTimeframe,
                  )?.label || timeframeOptions[0]?.label}
                </span>
                <span
                  className="material-symbols-outlined text-lg leading-none"
                  aria-hidden="true"
                >
                  arrow_drop_down
                </span>
              </div>
              <select
                id="measurement-trends-window"
                aria-label={t(
                  "profilePage.measurementTrendsWindowLabel",
                  "Trend window",
                )}
                className="absolute inset-0 cursor-pointer appearance-none opacity-0"
                value={selectedTimeframe}
                onChange={(event) => setSelectedTimeframe(event.target.value)}
              >
                {timeframeOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <button
              type="button"
              className="outlined-button !min-h-0 !px-4 !py-2 !text-sm"
              onClick={onClose}
            >
              {t("profilePage.closeOverlayButton", "Close")}
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-4">
          {loadingHistory ? (
            <LoadingSkeletonGrid t={t} />
          ) : state.loading ? (
            <div className="flex h-full items-center justify-center">
              <LoadingProgress
                completed={state.progressCompleted}
                total={state.progressTotal}
                t={t}
              />
            </div>
          ) : state.error ? (
            <div className="flex h-full items-center justify-center">
              <p className="max-w-md text-center text-red-500">
                {t(
                  "profilePage.measurementTrendsLoadError",
                  "We couldn't load the measurement trends. Please try again.",
                )}
              </p>
            </div>
          ) : !visibleCharts.length ? (
            <div className="flex h-full items-center justify-center">
              <p className="max-w-md text-center text-greyscale-3">
                {t(
                  "profilePage.noMeasurementTrends",
                  "No trend metrics are available for this patient yet.",
                )}
              </p>
            </div>
          ) : (
            <div className="flex flex-col gap-8">
              <TrendChartSection
                title={t(
                  "profilePage.measurementTrendQuestionnaireSection",
                  "Questionnaires",
                )}
                charts={chartsBySection.questionnaires}
                t={t}
                formatTooltipTitle={formatTooltipTitle}
                formatTooltipLabel={formatTooltipLabel}
                xDomainOverride={xDomain}
              />
              <TrendChartSection
                title={t(
                  "profilePage.measurementTrendMeasurementSection",
                  "Biomarkers",
                )}
                charts={chartsBySection.measurements}
                t={t}
                formatTooltipTitle={formatTooltipTitle}
                formatTooltipLabel={formatTooltipLabel}
                xDomainOverride={xDomain}
              />
              <TrendChartSection
                title={t(
                  "profilePage.measurementTrendQualitySection",
                  "Quality",
                )}
                charts={chartsBySection.quality}
                t={t}
                formatTooltipTitle={formatTooltipTitle}
                formatTooltipLabel={formatTooltipLabel}
                xDomainOverride={xDomain}
              />
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
